import { useEffect, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "../components/ui/accordion";
import { Package, Mail, User as UserIcon, LogOut, Truck } from "lucide-react";

const STATUS_STYLES = {
  pending: "bg-muted text-muted-foreground",
  confirmed: "bg-primary/20 text-secondary",
  shipped: "bg-primary text-primary-foreground",
  cancelled: "bg-destructive/15 text-destructive",
};

const PAYMENT_STATUS_STYLES = {
  pending: "bg-muted text-muted-foreground",
  paid: "bg-primary/20 text-secondary",
  failed: "bg-destructive/15 text-destructive",
  expired: "bg-muted text-muted-foreground line-through",
};

function formatDate(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleString(undefined, {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
}

export default function Account() {
  const { user, loading: authLoading, logout } = useAuth();
  const [orders, setOrders] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!user) return;
    api
      .get("/my/orders")
      .then((r) => setOrders(r.data))
      .catch(() => setError("Could not load your orders."));
  }, [user]);

  if (authLoading) {
    return (
      <div className="px-6 py-32 text-center text-muted-foreground" data-testid="account-loading">
        Loading account…
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  return (
    <div data-testid="account-page" className="animate-in fade-in duration-500">
      <section className="px-6 md:px-12 lg:px-24 pt-20 pb-12 border-b border-border">
        <p className="overline text-primary mb-4">Account</p>
        <h1 className="editorial-heading text-4xl md:text-5xl text-secondary">
          Welcome back, {user.full_name.split(" ")[0]}.
        </h1>
      </section>

      <section className="px-6 md:px-12 lg:px-24 py-16 grid grid-cols-1 md:grid-cols-12 gap-12">
        {/* SIDEBAR — profile */}
        <aside className="md:col-span-4">
          <div className="border border-border bg-card p-8" data-testid="account-profile-card">
            <p className="overline text-primary mb-5">Profile</p>
            <div className="space-y-4 text-sm mb-8">
              <div className="flex items-start gap-3">
                <UserIcon className="h-4 w-4 text-muted-foreground mt-0.5 shrink-0" />
                <div>
                  <p className="overline text-muted-foreground mb-1">Name</p>
                  <p className="text-secondary" data-testid="account-name">{user.full_name}</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <Mail className="h-4 w-4 text-muted-foreground mt-0.5 shrink-0" />
                <div className="min-w-0">
                  <p className="overline text-muted-foreground mb-1">Email</p>
                  <p className="text-secondary break-all" data-testid="account-email">{user.email}</p>
                </div>
              </div>
            </div>
            <Button
              onClick={logout}
              variant="outline"
              data-testid="account-logout"
              className="w-full rounded-none border-secondary text-secondary hover:bg-secondary hover:text-secondary-foreground uppercase tracking-[0.18em] text-xs py-5 flex items-center justify-center gap-2"
            >
              <LogOut className="h-4 w-4" /> Sign out
            </Button>
          </div>
        </aside>

        {/* ORDERS */}
        <div className="md:col-span-8">
          <div className="flex items-center justify-between mb-6">
            <h2 className="editorial-heading text-2xl md:text-3xl text-secondary">Order history</h2>
            {orders && (
              <span className="text-xs uppercase tracking-[0.18em] text-muted-foreground" data-testid="account-orders-count">
                {orders.length} {orders.length === 1 ? "order" : "orders"}
              </span>
            )}
          </div>

          {error && (
            <p className="text-sm text-destructive" data-testid="account-orders-error">
              {error}
            </p>
          )}

          {!orders && !error && (
            <p className="text-sm text-muted-foreground" data-testid="account-orders-loading">
              Loading orders…
            </p>
          )}

          {orders && orders.length === 0 && (
            <div className="border border-border bg-muted p-12 text-center" data-testid="account-orders-empty">
              <Package className="h-8 w-8 text-muted-foreground mx-auto mb-4" />
              <p className="text-secondary mb-2">No orders yet</p>
              <p className="text-sm text-muted-foreground mb-6">
                When you place your first order it will appear here.
              </p>
              <Link
                to="/shop"
                className="inline-flex items-center justify-center bg-secondary text-secondary-foreground px-6 py-3 uppercase tracking-[0.22em] text-xs hover:bg-primary hover:text-primary-foreground transition-colors"
                data-testid="account-orders-shop-link"
              >
                Browse the catalog
              </Link>
            </div>
          )}

          {orders && orders.length > 0 && (
            <Accordion type="multiple" className="border-t border-border" data-testid="account-orders-list">
              {orders.map((o) => (
                <AccordionItem
                  key={o.id}
                  value={o.id}
                  className="border-b border-border"
                  data-testid={`account-order-${o.id.slice(0, 8)}`}
                >
                  <AccordionTrigger className="hover:no-underline py-5">
                    <div className="flex-1 grid grid-cols-2 md:grid-cols-5 gap-4 text-left items-center">
                      <div>
                        <p className="overline text-muted-foreground mb-1">Order</p>
                        <p className="text-sm text-secondary font-medium">#{o.id.slice(0, 8)}</p>
                      </div>
                      <div className="hidden md:block">
                        <p className="overline text-muted-foreground mb-1">Date</p>
                        <p className="text-sm text-secondary">{formatDate(o.created_at)}</p>
                      </div>
                      <div className="hidden md:block">
                        <p className="overline text-muted-foreground mb-1">Items</p>
                        <p className="text-sm text-secondary">
                          {o.items.reduce((a, i) => a + i.quantity, 0)}
                        </p>
                      </div>
                      <div>
                        <p className="overline text-muted-foreground mb-1">Total</p>
                        <p className="text-sm text-secondary font-medium">${o.total.toFixed(2)}</p>
                      </div>
                      <div className="flex flex-col gap-1.5 items-start md:items-end">
                        <span
                          className={`text-[10px] uppercase tracking-[0.18em] px-2 py-0.5 ${
                            STATUS_STYLES[o.status] || STATUS_STYLES.pending
                          }`}
                          data-testid={`order-status-${o.id.slice(0, 8)}`}
                        >
                          {o.status}
                        </span>
                        <span
                          className={`text-[10px] uppercase tracking-[0.18em] px-2 py-0.5 ${
                            PAYMENT_STATUS_STYLES[o.payment_status] || PAYMENT_STATUS_STYLES.pending
                          }`}
                          data-testid={`order-payment-${o.id.slice(0, 8)}`}
                        >
                          {o.payment_status}
                        </span>
                      </div>
                    </div>
                  </AccordionTrigger>
                  <AccordionContent className="pb-6">
                    <div className="grid grid-cols-1 md:grid-cols-12 gap-8 pt-2">
                      <div className="md:col-span-7">
                        <p className="overline text-muted-foreground mb-4">Items</p>
                        <ul className="divide-y divide-border border-t border-border">
                          {o.items.map((it) => (
                            <li
                              key={it.product_id}
                              className="flex gap-4 py-3"
                              data-testid={`order-item-${it.slug}`}
                            >
                              <img src={it.image} alt={it.name} className="w-14 h-16 object-cover" />
                              <div className="flex-1 min-w-0">
                                <Link
                                  to={`/product/${it.slug}`}
                                  className="text-sm text-secondary hover:text-primary transition-colors block truncate"
                                >
                                  {it.name}
                                </Link>
                                <p className="text-xs text-muted-foreground">
                                  Qty {it.quantity} · ${it.price.toFixed(2)} ea
                                </p>
                              </div>
                              <p className="text-sm text-secondary">
                                ${(it.price * it.quantity).toFixed(2)}
                              </p>
                            </li>
                          ))}
                        </ul>
                        <div className="mt-5 space-y-1.5 text-sm">
                          <div className="flex justify-between">
                            <span className="text-muted-foreground">Subtotal</span>
                            <span className="text-secondary">${o.subtotal.toFixed(2)}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-muted-foreground">Shipping</span>
                            <span className="text-secondary">${o.shipping.toFixed(2)}</span>
                          </div>
                          <div className="flex justify-between font-medium pt-2 border-t border-border">
                            <span className="text-secondary">Total</span>
                            <span className="text-secondary">${o.total.toFixed(2)}</span>
                          </div>
                        </div>
                      </div>
                      <div className="md:col-span-5 space-y-6 text-sm">
                        <div>
                          <p className="overline text-muted-foreground mb-2">Payment</p>
                          <p className="text-secondary capitalize">
                            {o.payment_method.replace("_", " ")}
                          </p>
                        </div>
                        <div>
                          <p className="overline text-muted-foreground mb-2 flex items-center gap-2">
                            <Truck className="h-3 w-3" /> Shipping to
                          </p>
                          <p className="text-secondary">{o.customer.full_name}</p>
                          <p className="text-muted-foreground">
                            {o.customer.address_line1}
                            {o.customer.address_line2 ? `, ${o.customer.address_line2}` : ""}
                          </p>
                          <p className="text-muted-foreground">
                            {o.customer.city}, {o.customer.state} {o.customer.postal_code}
                          </p>
                          <p className="text-muted-foreground">{o.customer.country}</p>
                        </div>
                      </div>
                    </div>
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          )}
        </div>
      </section>
    </div>
  );
}
